import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const MEMBER_TYPES = [
  { value: 'AUTO_SHOP', label: 'Auto Shop' },
  { value: 'ITP_STATION', label: 'ITP Station' },
];

const INTEREST_LEVELS = [
  { value: 'UNDEFINED', label: 'Undefined', color: 'bg-gray-100 text-gray-800' },
  { value: 'NOT_INTERESTED', label: 'Not Interested', color: 'bg-red-100 text-red-800' },
  { value: 'INTERESTED', label: 'Interested', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'VERY_INTERESTED', label: 'Very Interested', color: 'bg-green-100 text-green-800' },
];

const STATUS_LEVELS = [
  { value: 'NOT_CONTACTED', label: 'Not Contacted', color: 'bg-gray-100 text-gray-600' },
  { value: 'CONTACTED', label: 'Contacted', color: 'bg-blue-100 text-blue-800' },
  { value: 'WAITING_MEMBER_COMM', label: 'Waiting Response', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'REQUIRES_ACTION', label: 'Requires Action', color: 'bg-red-100 text-red-800' },
  { value: 'PAYING_USER', label: 'Paying User', color: 'bg-green-100 text-green-800' },
];

const AudienceMemberForm = ({ show, member, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cui: '',
    type: 'AUTO_SHOP',
    interest: 'UNDEFINED',
    status: 'NOT_CONTACTED',
  });

  useEffect(() => {
    if (member) {
      const info = member.memberInfo || {};
      setFormData({
        name: info.name || '',
        email: info.email || '',
        phone: info.phone || '',
        cui: info.cui || '',
        type: member.type || 'AUTO_SHOP',
        interest: member.interest || 'UNDEFINED',
        status: member.status || 'NOT_CONTACTED',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        cui: '',
        type: 'AUTO_SHOP',
        interest: 'UNDEFINED',
        status: 'NOT_CONTACTED',
      });
    }
  }, [member]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      alert('Name and email are required');
      return;
    }

    if (member) {
      // Update existing member
      onSubmit({
        id: member.id,
        memberInfo: {
          ...member.memberInfo,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          cui: formData.cui,
        },
        type: formData.type,
        interest: formData.interest,
        status: formData.status,
      });
    } else {
      // Add new member (interest defaults to UNDEFINED on backend)
      onSubmit({
        memberInfo: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          cui: formData.cui || null,
        },
        type: formData.type,
      });
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {member ? 'Edit Member' : 'Add Member'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Name *
            </label>
            <input
              type="text"
              required
              className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email *
            </label>
            <input
              type="email"
              required
              className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Phone
            </label>
            <input
              type="tel"
              className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+40123456789"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              CUI (Company ID)
            </label>
            <input
              type="text"
              className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              value={formData.cui}
              onChange={(e) => setFormData({ ...formData, cui: e.target.value })}
              placeholder="RO12345678"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Type *
            </label>
            <div className="mt-2 flex gap-4">
              {MEMBER_TYPES.map((typeOption) => (
                <label key={typeOption.value} className="flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value={typeOption.value}
                    checked={formData.type === typeOption.value}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">{typeOption.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Interest Level - Only shown in edit mode */}
          {member && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Interest Level
              </label>
              <select
                value={formData.interest}
                onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              >
                {INTEREST_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Track the prospect's interest level for CRM purposes.
              </p>
            </div>
          )}

          {/* CRM Status - Only shown in edit mode */}
          {member && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                CRM Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              >
                {STATUS_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Track the communication status with this prospect.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              {member ? 'Update' : 'Add'} Member
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AudienceMemberForm;

